import { Loader, useTheme } from "@radianse/components";
import ErrorMsg from "../../common/ErrorMsg";
import merge from "lodash.merge";
import React from "react";
import {
  TokenApiClient,
  UserApiApiClient,
  UserApiClient,
} from "../../apiConfig";

import Welcome from "./Welcome";
import { appInsights } from "../../common/AppInsightsErrorBoundary/AppInsights";

export const DEVICE_ID = "device_id";
export const SESSION = "session_id";
export const TOKEN_NAME = "token_id";

const userPermissions = (user) => {
  return merge(
    user?.tenantPermissionsPerApp,
    user?.customerPermissionsPerApp,
    user?.groupPermissionsPerApp
  );
};

export const ApiAuthContext = React.createContext(undefined);

export function ApiAuthProvider({ children }) {
  const [, switchTheme] = useTheme();
  const params = new URLSearchParams(window.location.search);
  const deviceIdParam = params.get(DEVICE_ID);
  const sessionParam = params.get(SESSION);
  const deviceId = localStorage.getItem(DEVICE_ID);
  const session = localStorage.getItem(SESSION);
  const [authState, setAuth] = React.useState({
    status: "pending",
    error: null,
    user: null,
  });
  let token;
  function getClientToken(deviceIdC, sessionC) {
    TokenApiClient.getServiceClientToken("en-US,en", {
      clientId: deviceIdC,
      clientSecret: sessionC,
    })
      .then((res) => {
        if (res?.data?.token) {
          localStorage.setItem(TOKEN_NAME, res?.data?.token);
          token = res?.data?.token;
          getTenantId(res?.data?.token);
        } else if (res?.data?.error) {
          throw res?.data?.error;
        } else {
          throw "No Token";
        }
      })
      .catch((err) => {
        if (err === "invalid_client") {
          clearDevice();
          setAuth({
            status: "pending",
            error: null,
            user: "invalid",
          });
        } else {
          setAuth({
            status: "error",
            error: {
              message:
                "Failed to authorize. Please try again, or contact Radianse Support at 603-994-2222.",
            },
            user: null,
          });
        }
      });
  }
  function getTenantId() {
    UserApiApiClient.userApiClientTreeListGet(false, "1.0", "en-US,en", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_NAME)}`,
      },
    }).then((res) => {
      const tenantId =
        res?.data?.data?.find((item) => item.type === "Tenant")?.id || null;
      if (tenantId) {
        localStorage.setItem("tenant", tenantId);
        getUser(tenantId);
      } else {
        setAuth({
          status: "error",
          error: {
            message:
              "Unable to find a tenant associated with this device. Please contact Radianse Support at 603-994-2222.",
          },
          user: null,
        });
      }
    });
  }
  function getUser(tenantId) {
    UserApiClient.userMeGet(tenantId, "1.0", "en-US,en").then(
      (res) => {
        // TODO: Theme selection should be defined in the api
        if (res?.data?.data?.customerName) {
          switchTheme(
            res?.data?.data?.customerName?.replace(/\s/g, "").toLowerCase()
          );
        }
        const permissions = userPermissions(res.data.data);
        if (!Object.keys(permissions).length) {
          setAuth({
            status: "error",
            error: {
              message:
                "Failed to sync user permissions. Please try again, or contact Radianse Support at 603-994-2222.",
            },
            user: null,
          });
        } else {
          setAuth({
            status: "success",
            error: null,
            user: {
              token,
              ...res.data.data,
              permissions,
            },
          });
          appInsights.addTelemetryInitializer((env) => {
            env.tags = env.tags || [];
            env.data.user = res.data.data;
            env.data.permissions = permissions;
          });
        }
      },
      (error) => {
        setAuth({ status: "error", error, user: null });
      }
    );
  }
  function storeDevice(deviceId, session) {
    localStorage.setItem(DEVICE_ID, deviceId);
    localStorage.setItem(SESSION, session);
  }
  function clearDevice() {
    localStorage.removeItem(DEVICE_ID);
    localStorage.removeItem(SESSION);
    localStorage.removeItem(TOKEN_NAME);
  }

  React.useEffect(() => {
    if (deviceId && session) {
      getClientToken(deviceId, session);
    } else if (deviceIdParam && sessionParam) {
      storeDevice(deviceIdParam, sessionParam);
      window.location.href = window.location.origin;
    }
  }, [deviceId, session]);

  if (!deviceId || !session) {
    return <Welcome />;
  }
  return (
    <ApiAuthContext.Provider value={authState}>
      {authState.status === "pending" ? (
        <Loader text={"Logging in..."} />
      ) : authState.status === "error" ? (
        <ErrorMsg
          title={"Authentication failed"}
          message={authState.error.message}
        >
          Please contact Radianse Support at 603-994-2222.
        </ErrorMsg>
      ) : (
        children
      )}
    </ApiAuthContext.Provider>
  );
}

export function useApiAuthState() {
  const authState = React.useContext(ApiAuthContext);

  const isPending = authState.status === "pending";
  const isError = authState.status === "error";
  const isSuccess = authState.status === "success";
  const isAuthenticated = authState.user && isSuccess;
  return {
    ...authState,
    isPending,
    isError,
    isSuccess,
    isAuthenticated,
  };
}

export function useApiUser() {
  const { user } = React.useContext(ApiAuthContext);
  return user;
}

export function useApiCustomer() {
  const {
    user: { customerId, customerName },
  } = React.useContext(ApiAuthContext);
  return { customerId, customerName };
}

export function useApiTenant() {
  const {
    user: { tenantId, tenantName },
  } = React.useContext(ApiAuthContext);
  return { tenantId, tenantName };
}

export function useApiGroup() {
  const {
    user: { groupId, groupName },
  } = React.useContext(ApiAuthContext);
  return { groupId, groupName };
}
